import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMessage } from '../../contexts/MessageContext'
import { useApi } from '../../utils/api'
import { FormLabel, FormSelect, FormInput, FormButton, CancelButton } from '../../components/forms/FormElements'
import { PageTitleBar } from '../../components/PageTitleBar'

interface FormData {
  status: string;
  adminLevel: string;
  name: string;
  email: string;
  password: string;
}

export default function NewAdminUser() {
  const { setMessage } = useMessage()
  const { fetchWithAuth } = useApi()
  const [formData, setFormData] = useState<FormData>({
    status: 'active',
    adminLevel: 'admin',
    name: '',
    email: '',
    password: '',
  })
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prevData => ({ ...prevData, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/admin-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          admin_level: formData.adminLevel === 'superadmin' ? 'super_admin' : 'admin',
          is_active: formData.status === 'active' ? 1 : 0,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to create admin user')
      }

      setMessage('Admin user successfully created!')
      navigate('/admin-users')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <main className="container mx-auto px-4 py-8">
        <PageTitleBar
          title="New Admin User"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Admin Users', href: '/admin-users' },
            { label: 'New User' },
          ]}
        />

        <div className="bg-white rounded-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <FormLabel htmlFor="status">STATUS</FormLabel>
              <FormSelect
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </FormSelect>
            </div>

            <div>
              <FormLabel htmlFor="adminLevel">ADMIN LEVEL</FormLabel>
              <FormSelect
                id="adminLevel"
                name="adminLevel"
                value={formData.adminLevel}
                onChange={handleChange}
                required
              >
                <option value="">Select Admin Level</option>
                <option value="superadmin">Super Admin</option>
                <option value="admin">Admin</option>
              </FormSelect>
            </div>

            <div>
              <FormLabel htmlFor="name">NAME</FormLabel>
              <FormInput
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <FormLabel htmlFor="email">EMAIL</FormLabel>
              <FormInput
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <FormLabel htmlFor="password">PASSWORD</FormLabel>
              <FormInput
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <p className="mt-1 text-sm text-gray-500">Minimum 5 characters with at least 1 letter and 1 number</p>
            </div>

            <div className="flex justify-end space-x-3">
              <CancelButton type="button" onClick={() => navigate('/admin-users')}>
                Cancel
              </CancelButton>
              <FormButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </FormButton>
            </div>
            {error && <div className="text-red-500 mt-2">{error}</div>}
          </form>
        </div>
      </main>
    </div>
  )
}
